import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { contactLogsApi, ContactLogListParams } from '@/api/contact-logs';
import { ContactLog, ContactLogRequest } from '@/types/api';
import { toast } from 'sonner';
import { DEFAULT_PAGE_SIZE } from '@/utils/pagination';

// Query keys
export const contactLogKeys = {
  all: ['contact-logs'] as const,
  lists: () => [...contactLogKeys.all, 'list'] as const,
  list: (params?: ContactLogListParams) => [...contactLogKeys.lists(), params] as const,
  details: () => [...contactLogKeys.all, 'detail'] as const,
  detail: (id: number) => [...contactLogKeys.details(), id] as const,
  byContact: (contactId: number) => [...contactLogKeys.all, 'by-contact', contactId] as const,
};

// Hook to get contact logs list
export const useContactLogs = (params?: ContactLogListParams, queryOptions?: any) => {
  return useQuery({
    queryKey: contactLogKeys.list(params),
    queryFn: () => contactLogsApi.list(params),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    ...queryOptions,
  });
};

// Hook to get a single contact log
export const useContactLog = (id: number) => {
  return useQuery({
    queryKey: contactLogKeys.detail(id),
    queryFn: () => contactLogsApi.getById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook to get contact logs by contact
export const useContactLogsByContact = (contactId: number, enabled = true) => {
  return useQuery({
    queryKey: contactLogKeys.byContact(contactId),
    queryFn: () => contactLogsApi.getByContact(contactId),
    enabled: enabled && !!contactId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook to get contact logs list with infinite pagination
export const useContactLogsInfinite = (params?: Omit<ContactLogListParams, 'page' | 'page_size'>) => {
  return useInfiniteQuery({
    queryKey: contactLogKeys.list(params),
    queryFn: ({ pageParam = 1 }) =>
      contactLogsApi.list({
        ...params,
        page: pageParam,
        page_size: DEFAULT_PAGE_SIZE
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || !allPages) return undefined;
      if (lastPage.next) {
        return allPages.length + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook to get contact logs for a specific contact with infinite pagination
export const useContactLogsByContactInfinite = (contactId: number, ordering?: string, mood?: string) => {
  return useInfiniteQuery({
    queryKey: [...contactLogKeys.byContact(contactId), ordering, mood],
    queryFn: ({ pageParam = 1 }) =>
      contactLogsApi.list({
        contact: contactId,
        page: pageParam,
        page_size: DEFAULT_PAGE_SIZE,
        ordering: ordering || '-log_date',
        mood: mood !== 'all' ? mood : undefined,
      }),
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage || !allPages) return undefined;
      if (lastPage.next) {
        return allPages.length + 1;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

// Hook to create a contact log
export const useCreateContactLog = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ContactLogRequest) => contactLogsApi.create(data),
    onSuccess: (data) => {
      // Invalidate and refetch contact logs list
      queryClient.invalidateQueries({ queryKey: contactLogKeys.lists() });
      // Invalidate logs for the specific contact
      queryClient.invalidateQueries({ queryKey: contactLogKeys.byContact(data.contact) });
      // Success notification handled by component
    },
    onError: (error) => {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to create contact log';
      toast.error(errorMessage);
    },
  });
};

// Hook to update a contact log
export const useUpdateContactLog = (id: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ContactLogRequest) => contactLogsApi.update(id, data),
    onSuccess: (data) => {
      // Update the cached contact log data
      queryClient.setQueryData(contactLogKeys.detail(id), data);
      // Invalidate contact logs list
      queryClient.invalidateQueries({ queryKey: contactLogKeys.lists() });
      // Invalidate logs for the specific contact
      queryClient.invalidateQueries({ queryKey: contactLogKeys.byContact(data.contact) });
      toast.success('Contact log updated successfully');
    },
    onError: (error) => {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to update contact log';
      toast.error(errorMessage);
    },
  });
};

// Hook to partially update a contact log
export const usePartialUpdateContactLog = (id: number) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<ContactLogRequest>) => contactLogsApi.partialUpdate(id, data),
    onSuccess: (data) => {
      // Update the cached contact log data
      queryClient.setQueryData(contactLogKeys.detail(id), data);
      // Invalidate contact logs list
      queryClient.invalidateQueries({ queryKey: contactLogKeys.lists() });
      // Invalidate logs for the specific contact
      queryClient.invalidateQueries({ queryKey: contactLogKeys.byContact(data.contact) });
      toast.success('Contact log updated successfully');
    },
    onError: (error) => {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to update contact log';
      toast.error(errorMessage);
    },
  });
};

// Hook to delete a contact log
export const useDeleteContactLog = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => contactLogsApi.delete(id),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: contactLogKeys.detail(id) });
      // Invalidate contact logs list
      queryClient.invalidateQueries({ queryKey: contactLogKeys.lists() });
      // Note: We can't invalidate byContact without knowing which contact,
      // but the list invalidation should be sufficient
      toast.success('Contact log deleted successfully');
    },
    onError: (error) => {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to delete contact log';
      toast.error(errorMessage);
    },
  });
};
